import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table} from "react-bootstrap";
import {Link} from "react-router-dom";
import HeroSection from "../../../components/HeroSection.jsx";
import {reportsApi} from "../../../api/reports.js";
import {exportReportToCsv, exportReportToXlsx, openReportPrintWindow} from "../../../_helpers/reportExport.js";

function formatLocalDateInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getCurrentMonthDateRange() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
        dateFrom: formatLocalDateInput(firstDay),
        dateTo: formatLocalDateInput(lastDay)
    };
}

function MonthlySalesSummaryReportPage() {
    const defaultRange = getCurrentMonthDateRange();
    const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
    const [dateTo, setDateTo] = useState(defaultRange.dateTo);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [monthlySummary, setMonthlySummary] = useState(null);

    const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
    const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");
    const formatDateForApi = (value, isEndOfDay = false) => {
        if (!value) {
            return undefined;
        }
        return isEndOfDay ? `${value}T23:59:59.999` : `${value}T00:00:00`;
    };

    const getReportParams = (overrides = {}) => ({
        dateFrom: formatDateForApi(overrides.dateFrom ?? dateFrom),
        dateTo: formatDateForApi(overrides.dateTo ?? dateTo, true)
    });

    const validateDateRange = (fromValue, toValue) => {
        if (!fromValue || !toValue) {
            setError("Date From and Date To are required.");
            setMonthlySummary(null);
            setLoading(false);
            return false;
        }

        if (fromValue > toValue) {
            setError("Date From cannot be after Date To.");
            setMonthlySummary(null);
            setLoading(false);
            return false;
        }

        return true;
    };

    const loadReport = async (overrides = {}) => {
        const fromValue = overrides.dateFrom ?? dateFrom;
        const toValue = overrides.dateTo ?? dateTo;

        if (!validateDateRange(fromValue, toValue)) {
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await reportsApi.getTaxAuthorityMonthlySalesSummary(getReportParams({
                dateFrom: fromValue,
                dateTo: toValue
            }));
            setMonthlySummary(response);
        } catch (e) {
            console.error("Failed to load monthly sales summary", e);
            setError("Failed to load report. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSections = () => {
        if (!monthlySummary) {
            return [];
        }

        return [
            {
                title: "Overall Totals",
                sheetName: "Overall Totals",
                columns: ["Transactions", "Items", "Revenue"],
                rows: [[
                    monthlySummary.totals?.transactions || 0,
                    monthlySummary.totals?.items || 0,
                    Number(monthlySummary.totals?.revenue || 0).toFixed(2)
                ]]
            },
            {
                title: "Sales by Day",
                sheetName: "Sales By Day",
                columns: ["Date", "Transactions", "Items", "Revenue"],
                rows: (monthlySummary.byDay || []).map((row) => [
                    formatDate(row.date),
                    row.transactions,
                    row.items,
                    Number(row.revenue || 0).toFixed(2)
                ]),
                footerRows: [[
                    "Rollup",
                    monthlySummary.totals?.transactions || 0,
                    monthlySummary.totals?.items || 0,
                    Number(monthlySummary.totals?.revenue || 0).toFixed(2)
                ]]
            },
            {
                title: "Sales by Month",
                sheetName: "Sales By Month",
                columns: ["Month", "Transactions", "Items", "Revenue"],
                rows: (monthlySummary.byMonth || []).map((row) => [
                    new Date(row.month).toLocaleDateString(undefined, {month: "long", year: "numeric"}),
                    row.transactions,
                    row.items,
                    Number(row.revenue || 0).toFixed(2)
                ]),
                footerRows: [[
                    "Rollup",
                    monthlySummary.totals?.transactions || 0,
                    monthlySummary.totals?.items || 0,
                    Number(monthlySummary.totals?.revenue || 0).toFixed(2)
                ]]
            }
        ];
    };

    const exportReport = (format) => {
        const sections = getSections();
        if (!sections.length) {
            return;
        }

        if (format === "csv") {
            exportReportToCsv("monthly_sales_summary.csv", sections);
            return;
        }

        if (format === "xlsx") {
            exportReportToXlsx("monthly_sales_summary.xlsx", sections);
            return;
        }

        openReportPrintWindow("Monthly Sales Summary", sections);
    };

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="muni"
                eyebrow="Reports"
                title="Monthly Sales Summary"
                subtitle="Summarize completed billable municipality revenue by day and month."
                actions={
                    <div className="d-flex gap-2">
                        <Button as={Link} to="/taxAuth/reports" variant="outline-secondary">All Reports</Button>
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                const resetRange = getCurrentMonthDateRange();
                                setDateFrom(resetRange.dateFrom);
                                setDateTo(resetRange.dateTo);
                                loadReport(resetRange);
                            }}
                        >
                            Reset Dates
                        </Button>
                        <Button variant="primary" onClick={loadReport}>Refresh</Button>
                    </div>
                }
            />

            <Card className="dashboard-panel mt-3">
                <Card.Header className="bg-white"><strong>Filters</strong></Card.Header>
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label>Date From</Form.Label>
                            <Form.Control type="date" required value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <Form.Label>Date To</Form.Label>
                            <Form.Control type="date" required value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </Col>
                        <Col md="auto">
                            <Button variant="primary" onClick={loadReport}>Apply</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {loading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading report...</span>
                </div>
            )}

            {!loading && monthlySummary && (
                <Card className="dashboard-panel mt-3">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <strong>Monthly Sales Summary</strong>
                            <div className="small text-muted">Daily and monthly rollups for completed billable sales using municipality product-fee revenue only.</div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg="light" text="dark">{monthlySummary.totals?.transactions || 0} transactions</Badge>
                            <Button size="sm" variant="outline-secondary" onClick={() => exportReport("xlsx")}>Export XLSX</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => exportReport("csv")}>Export CSV</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => exportReport("pdf")}>Export PDF</Button>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3 text-center mb-4">
                            <Col md={2}>
                                <div className="small text-muted">Transactions</div>
                                <div className="h4">{monthlySummary.totals?.transactions || 0}</div>
                            </Col>
                            <Col md={2}>
                                <div className="small text-muted">Items</div>
                                <div className="h4">{monthlySummary.totals?.items || 0}</div>
                            </Col>
                            <Col md={2}>
                                <div className="small text-muted">Revenue</div>
                                <div className="h4">{formatMoney(monthlySummary.totals?.revenue)}</div>
                            </Col>
                        </Row>

                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Sales by Day</strong>
                                <Badge bg="light" text="dark">{monthlySummary.byDay?.length || 0} days</Badge>
                            </div>
                            <Table hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th className="text-end">Transactions</th>
                                        <th className="text-end">Items</th>
                                        <th className="text-end">Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(monthlySummary.byDay || []).map((row) => (
                                        <tr key={row.date}>
                                            <td>{formatDate(row.date)}</td>
                                            <td className="text-end">{row.transactions}</td>
                                            <td className="text-end">{row.items}</td>
                                            <td className="text-end">{formatMoney(row.revenue)}</td>
                                        </tr>
                                    ))}
                                    {!monthlySummary.byDay?.length && (
                                        <tr><td colSpan={4} className="text-center text-muted py-3">No data</td></tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th>Rollup</th>
                                        <th className="text-end">{monthlySummary.totals?.transactions || 0}</th>
                                        <th className="text-end">{monthlySummary.totals?.items || 0}</th>
                                        <th className="text-end">{formatMoney(monthlySummary.totals?.revenue)}</th>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>

                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Sales by Month</strong>
                                <Badge bg="light" text="dark">{monthlySummary.byMonth?.length || 0} months</Badge>
                            </div>
                            <Table hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th className="text-end">Transactions</th>
                                        <th className="text-end">Items</th>
                                        <th className="text-end">Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(monthlySummary.byMonth || []).map((row) => (
                                        <tr key={row.month}>
                                            <td>{new Date(row.month).toLocaleDateString(undefined, {month: "long", year: "numeric"})}</td>
                                            <td className="text-end">{row.transactions}</td>
                                            <td className="text-end">{row.items}</td>
                                            <td className="text-end">{formatMoney(row.revenue)}</td>
                                        </tr>
                                    ))}
                                    {!monthlySummary.byMonth?.length && (
                                        <tr><td colSpan={4} className="text-center text-muted py-3">No data</td></tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th>Rollup</th>
                                        <th className="text-end">{monthlySummary.totals?.transactions || 0}</th>
                                        <th className="text-end">{monthlySummary.totals?.items || 0}</th>
                                        <th className="text-end">{formatMoney(monthlySummary.totals?.revenue)}</th>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
}

export default observer(MonthlySalesSummaryReportPage);
